import React, { useEffect, lazy, useState } from 'react';
import {
  ReportChecklistAccessoriesData,
  ReportChecklistRequest,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useReportChecklist } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import './checklist.less';

import ReportChecklistBoshHeader from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistBoshHeader';
import ReportChecklistDescription from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistDescription';
import ReportChecklistDamage from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistDamage';
import ReportChecklistHeader from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistHeader';
import ReportChecklistAccessories from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistAccessories';
import ReportChecklistFooter from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistFooter';
import ReportChecklistRequestServices from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistRequestServices';
import ReportChecklistQuickChecklist from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistQuickChecklist';
import ReportChecklistComplementItems from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistComplementItems';
import ReportChecklistBoschAccessories from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistBoschAccessories';
import { useSignature } from '@/core/domain/workorder/ChecklistSignature/ChecklistSignature.hooks';
import { useAccessoryList } from '@/core/domain/people/Accessory/Accessory.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useCompanyReportConfig } from '@/core/domain/enterprise/Companies/CompaniesReportConfig/CompaniesReportConfig.hooks';
import { Button } from 'antd';
import ReportChecklistImages from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistImages';
import { Accessory } from '@/core/domain/people/Accessory/Accessory.types';
import { isAppPortocar } from '@/index';
import ReportChecklistPortoComplementItems from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistPortoComplementItems';
import ReportChecklistObservations from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistObservations';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

type SearchParams = Record<keyof ReportChecklistRequest, string>;

const ChecklistReport: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();
  const params = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;
  const [headerLoaded, setHeaderLoaded] = useState(false);
  const [damageLoaded, setDamageLoaded] = useState(false);

  const {
    data: checklist,
    isLoading,
    mutateAsync: getChecklist,
  } = useReportChecklist();

  const hasCustomerData = !!checklist?.customer;

  const accessoriesList = useAccessoryList({
    active: true,
    checkList: true,
    rows: 30,
  });

  const accessoriesData = flatInfiniteData(accessoriesList.data);

  const filterCheckedIfIsPorto = (item: Accessory) =>
    !isAppPortocar ||
    (isAppPortocar &&
      checklist?.accessories?.some(
        ({ accessoryId }) => accessoryId === item.id,
      ));

  const accessories: ReportChecklistAccessoriesData[] = accessoriesData
    .filter(filterCheckedIfIsPorto)
    .map((item) => {
      const find = checklist?.accessories?.find(
        ({ accessoryId }) => accessoryId === item.id,
      );
      return {
        accessoryId: item.id,
        comment: find?.comment || '',
        description: item.description,
        selected: !!find,
      };
    });

  const { data: signatureData, isLoading: signatureLoading } = useSignature({
    diagnosticId: params.diagnosticId ? Number(params.diagnosticId) : undefined,
    workOrderComplementId: params.workOrderComplementId
      ? Number(params.workOrderComplementId)
      : undefined,
  });

  const isBosch = checklist?.company?.bosch;

  const { data: configCompany } = useCompanyReportConfig(
    !!params.companyId ? String(params.companyId) : undefined,
  );

  useEffect(() => {
    if (params.diagnosticId || params.workOrderComplementId) {
      getChecklist({
        diagnosticId: Number(params.diagnosticId) || undefined,
        workOrderComplementId:
          Number(params.workOrderComplementId) || undefined,
      });
    }
  }, []);

  useEffect(() => {
    if (
      headerLoaded &&
      damageLoaded &&
      !!checklist?.company &&
      !signatureLoading
    ) {
      setTimeout(() => {
        window.print();
      }, 500);

      document.title = `Checklist_${
        checklist.company?.bosch ? 'Bosch' : ''
      }_${checklist.company?.name.split(' ').join('-')}`;
    }
  }, [headerLoaded, damageLoaded, checklist, signatureData]);

  const dontHaveItemId = !params.diagnosticId && !params.workOrderComplementId;

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {(!params.companyId || dontHaveItemId) && (
        <div className="modal-loading-overlay">
          <div style={{ textAlign: 'center' }}>
            <h2>
              <strong>Não encontramos informações de consulta</strong>
            </h2>
            {!params.companyId && !dontHaveItemId && (
              <p>A url está faltando dado da empresa.</p>
            )}
            {!!params.companyId && dontHaveItemId && (
              <p>A url está faltando dado do checklist.</p>
            )}
            <Button onClick={() => window.close()} type="link">
              Fechar checklist
            </Button>
          </div>
        </div>
      )}
      {match.isExact &&
        !isLoading &&
        !!checklist &&
        !!configCompany &&
        !accessoriesList.isLoading && (
          <TemplateReport>
            <main className="reportChecklist">
              {!checklist.worKOrder && (
                <div className="modal-loading-overlay">
                  <div style={{ textAlign: 'center' }}>
                    <h2>
                      <strong>
                        O checklist do orçamento selecionado não há informações.
                      </strong>
                    </h2>
                    <p>
                      Preencha os dados do checklist para poder fazer a
                      impressão
                    </p>
                    <Button onClick={() => window.close()} type="link">
                      Fechar checklist
                    </Button>
                  </div>
                </div>
              )}
              {isBosch ? (
                <ReportChecklistBoshHeader
                  checklist={checklist}
                  setLoaded={setHeaderLoaded}
                  companyConfig={configCompany}
                />
              ) : (
                <ReportChecklistHeader
                  company={checklist.company}
                  setLoaded={setHeaderLoaded}
                  companyConfig={configCompany}
                />
              )}

              <ReportChecklistDescription
                checklist={checklist}
                infoCustomerVisible={hasCustomerData}
              />

              <div style={!hasCustomerData ? { marginTop: 20 } : {}}>
                <ReportChecklistDamage
                  checklist={checklist}
                  setLoaded={setDamageLoaded}
                />
              </div>

              {isBosch && (
                <ReportChecklistBoschAccessories checklist={checklist} />
              )}

              {!isBosch && (
                <ReportChecklistAccessories accessories={accessories} />
              )}

              <ReportChecklistRequestServices checklist={checklist} />

              <ReportChecklistQuickChecklist checklist={checklist} />

              {isAppPortocar ? (
                <ReportChecklistPortoComplementItems checklist={checklist} />
              ) : (
                <ReportChecklistComplementItems checklist={checklist} />
              )}

              <ReportChecklistImages checklist={checklist} />

              <ReportChecklistObservations checklist={checklist} />

              <ReportChecklistFooter src={signatureData?.signature} />
            </main>
          </TemplateReport>
        )}
    </React.Suspense>
  );
};

export default ChecklistReport;
